import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { decodeTime } from 'ulid';
import ChevronLeftIcon from 'core/assets/svg/ic-chevron-left.svg';
import Label from 'core/components/label/label';
import Button from 'core/components/button/button';
import CollapsibleSection from 'core/components/collapsible-section';
import { useToast } from 'core/components/toast';
import DownloadButton from 'tools/helpers/generate-task-ui/components/download-button';
import { aorRunRequest, approveAorRunRequest } from '../aor.service';
import ApproveAndApplyDialog from '../aor-runs-table/approve-and-apply-dialog';
import RunArtifacts from '../aor-runs-table/run-artifacts';
import PreviewProgressDialog from '../preview-progress-dialog';
import { useAorPreview } from '../use-aor-preview';
import {
  COUNT_LABELS,
  canApproveAndApply,
  canRunPreview,
  formatDateTime,
  isPreviewFailed,
  statusText
} from '../aor-runs-table/helpers';
import { useTaskLiveFeed } from '../use-task-live-feed';
import ApplyResultsTable from './apply-results-table';
import RunTimeline from './run-timeline';
import { RESULT_VARIANTS, splitRowsByVariant } from './result-variants';
import useStyles from './aor-run-detail.styles';

const RUN_POLL_MS = 15000;

/**
 * An `approved` row already carries an apply task run id, because the approve route mints it before
 * the machine resumes. Presence is therefore NOT proof that an apply began — gate on the status the
 * machine actually reached, or a run parked for approval renders an empty apply section.
 */
const APPLY_REACHED_STATUSES = ['applying', 'applied', 'partially_applied', 'failed'];
const APPLY_TERMINAL_STATUSES = ['applied', 'partially_applied', 'failed'];

/** The statuses the page polls through, because the machine moves them without the operator acting. */
const POLLED_STATUSES = ['approved', 'applying'];

/** Friendly descriptions for the apply report CSVs — the description is the download link. */
const REPORT_CSV_LABELS = {
  success: 'Applied records — per-op outcomes and prices (full CSV)',
  failed: 'Failed / aborted records — with error detail (full CSV)',
  error: 'Machine-level failures (full CSV)'
};

/**
 * Same query tool-detail.jsx uses — the apply's stored rows live in the task-run tables. `startedAt`
 * is the apply's true start: the ULID is minted at APPROVE now, so its embedded time is the approval
 * instant and cannot date the apply.
 */
const GET_TOOL_DETAIL = gql`
  query getToolDetail($taskRunId: String!, $taskYYMM: String!) {
    getToolDetail(taskRunId: $taskRunId, taskYYMM: $taskYYMM) {
      taskRunId
      status
      startedAt
      endedAt
      startedBy
      data {
        type
        outputPayload
        filePaths {
          path
          output
        }
      }
    }
  }
`;

/**
 * `taskYYMM` is stamped at ensure-row time FROM the ULID, not from the resume clock, precisely so a
 * run parked across a month boundary keeps this derivation exact. Do not derive the month any other way.
 */
// UTC getters: applyKickoff builds the same key on Lambda, which runs in UTC, not the browser's zone.
export const applyTaskYYMM = (applyTaskRunId) => {
  const startedAt = new Date(decodeTime(applyTaskRunId));
  return `aorApply-${startedAt.getUTCFullYear()}-${startedAt.getUTCMonth() + 1}`;
};

const parsePayload = (outputPayload) => {
  if (!outputPayload) {
    return null;
  }
  try {
    return typeof outputPayload === 'string' ? JSON.parse(outputPayload) : outputPayload;
  } catch {
    return null;
  }
};

/**
 * One AOR run, end to end: its lifecycle timeline, blast-radius counts, the snapshot it covers, the
 * single Approve & apply gesture, a live per-item feed while applying, and the per-record results
 * once the apply lands.
 *
 * Only the APPLY feed is live here. The preview is always over by the time this page can open — the
 * aor_runs row does not exist until WriteAorRun, which runs after the enumeration finishes — so the
 * preview contributes its stored snapshot CSVs instead, through RunArtifacts.
 */
const AorRunDetail = () => {
  const classes = useStyles();
  const toast = useToast();
  const { runId } = useParams();
  const [run, setRun] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [showApprove, setShowApprove] = useState(false);
  const [approveError, setApproveError] = useState(null);
  const [showPreviewProgress, setShowPreviewProgress] = useState(false);
  // Set from the approve response so the feed attaches before the arming PATCH, not after a poll.
  const [justApprovedTaskRunId, setJustApprovedTaskRunId] = useState(null);

  const fetchRun = useCallback(async () => {
    try {
      setRun(await aorRunRequest(runId));
      setLoadError(null);
    } catch (error) {
      setLoadError(error.message);
    }
  }, [runId]);

  useEffect(() => {
    fetchRun();
  }, [fetchRun]);

  // An approved run arms itself and then finishes without the operator doing anything, so poll both.
  useEffect(() => {
    if (!POLLED_STATUSES.includes(run?.status)) {
      return undefined;
    }
    const interval = setInterval(fetchRun, RUN_POLL_MS);
    return () => clearInterval(interval);
  }, [run?.status, fetchRun]);

  const applyReached = APPLY_REACHED_STATUSES.includes(run?.status);

  const {
    data: detailData,
    refetch: refetchResults,
    error: resultsError
  } = useQuery(GET_TOOL_DETAIL, {
    variables: run?.applyTaskRunId
      ? { taskRunId: run.applyTaskRunId, taskYYMM: applyTaskYYMM(run.applyTaskRunId) }
      : undefined,
    skip: !run?.applyTaskRunId || !applyReached,
    fetchPolicy: 'no-cache'
  });

  const toolDetail = detailData?.getToolDetail;

  // The results query keeps its pre-completion answer when the feed's terminal message is missed.
  const previousStatusRef = useRef(run?.status);
  useEffect(() => {
    const previousStatus = previousStatusRef.current;
    previousStatusRef.current = run?.status;
    if (POLLED_STATUSES.includes(previousStatus) && APPLY_TERMINAL_STATUSES.includes(run?.status)) {
      refetchResults();
    }
  }, [run?.status, refetchResults]);

  const applyFeed = useTaskLiveFeed(justApprovedTaskRunId ?? (applyReached ? run?.applyTaskRunId : null), {
    onTerminal: () => {
      fetchRun();
      refetchResults();
    }
  });

  const preview = useAorPreview({
    onComplete: () => {
      // A fresh cycle creates a NEW run row — this page still shows the old one.
      toast.notify({ type: 'success', message: 'Preview complete — the new run is on the AOR Runs tab.' });
      fetchRun();
    }
  });

  const feedMessages = applyFeed.messages;
  // Every branch's worker emits one `<recordId>: <op>=<outcome>` line — policies lead with nrat=,
  // offers and members with affinity=, staff users with stems=. Count them all, or a run made up of
  // one entity reads as no progress at all. A new branch MUST reuse one of these tokens rather than
  // inventing a fourth.
  const progressCount = feedMessages.filter((message) => /: (nrat|affinity|stems)=/.test(message)).length;
  // Absence and a true zero are different facts — applyKickoff refuses to start a run on a missing
  // count, so don't render a confident denominator built from half the data.
  const counts = run?.counts;
  const hasAllCounts =
    typeof counts?.policies === 'number' &&
    typeof counts?.offers === 'number' &&
    typeof counts?.members === 'number' &&
    typeof counts?.staffUsers === 'number';
  const recordCount = hasAllCounts ? counts.policies + counts.offers + counts.members + counts.staffUsers : null;

  const results = useMemo(() => {
    const outputRow = (toolDetail?.data ?? []).find((row) => row.type === 'OUTPUT' && row.outputPayload);
    if (!outputRow) {
      return null;
    }
    const payload = parsePayload(outputRow.outputPayload);
    if (!payload) {
      return null;
    }
    // >1000 rows the stored payload degrades to a flat, truncated array — the CSVs are the source then.
    if (Array.isArray(payload)) {
      return { partial: true, applied: null, failed: null, filePaths: outputRow.filePaths ?? [] };
    }
    // Bucketed per entity: the apply reports every branch through one merged manifest, so a payload
    // mixes policy, offer, member and staff-user rows.
    return {
      partial: false,
      applied: splitRowsByVariant(payload.success ?? []),
      failed: splitRowsByVariant([...(payload.failed ?? []), ...(payload.error ?? [])]),
      filePaths: outputRow.filePaths ?? []
    };
  }, [toolDetail]);

  const handleConfirmApproveAndApply = useCallback(
    async ({ repUsername, updateType }) => {
      setApproveError(null);
      try {
        const approved = await approveAorRunRequest({ runId: run.id, repUsername, updateType });
        if (approved.applyTaskRunId) {
          setJustApprovedTaskRunId(approved.applyTaskRunId);
        }
        toast.notify({ type: 'success', message: `Run approved by ${approved.approvedBy} — applying now.` });
        setShowApprove(false);
      } catch (error) {
        // A 409 is expected here (already approved, mappings changed, window closed). Its wording is
        // the operator's instruction, so keep it in the dialog rather than a toast that scrolls away.
        setApproveError(error.detail ?? error.message);
      } finally {
        await fetchRun();
      }
    },
    [fetchRun, run, toast]
  );

  const handleRunPreview = useCallback(async () => {
    setShowPreviewProgress(true);
    await preview.start(run.aorRequestId);
  }, [preview, run]);

  if (loadError) {
    return <Label type="infoLabel">{`Could not load the AOR run: ${loadError}`}</Label>;
  }
  if (!run) {
    return <Label type="infoLabel">Loading AOR run…</Label>;
  }

  return (
    <div className={classes.page}>
      <Link to="/staff/tools/dashboards/affinities-tools?tab=aor-runs" className={classes.backLink}>
        <img src={ChevronLeftIcon} alt="" className={classes.backIcon} />
        Back to AOR Runs
      </Link>
      <div className={classes.headerRow}>
        <div>
          <Label type="infoTitle">{`AOR run · ${statusText(run)}`}</Label>
          <Label type="infoLabel">{`Request ${run.aorRequestId}`}</Label>
        </div>
        <div className={classes.actions}>
          {canApproveAndApply(run) && (
            <Button
              className={classes.actionButton}
              variant="contained"
              color="primary"
              onClick={() => {
                setApproveError(null);
                setShowApprove(true);
              }}
            >
              Approve &amp; apply
            </Button>
          )}
          {canRunPreview(run) && (
            <Button className={classes.actionButton} variant="contained" color="secondary" onClick={handleRunPreview}>
              Run preview
            </Button>
          )}
        </div>
      </div>

      {isPreviewFailed(run) ? (
        // Nothing was enumerated, so a timeline, a blast radius and a snapshot would all be false.
        <div className={classes.section}>
          <Label type="infoTitleSmall">Preview failed</Label>
          <Label type="infoValue">{run.notes || 'The preview did not finish, so no records were enumerated.'}</Label>
          <Label type="infoLabel">Run preview to try again. The preview task run id is under Additional details.</Label>
        </div>
      ) : (
        <>
          <div className={classes.section}>
            <Label type="infoTitleSmall">Lifecycle</Label>
            <RunTimeline run={run} applyStartedAt={toolDetail?.startedAt} />
            {run.notes && (
              <div className={classes.metaItem}>
                <Label type="infoLabel">Notes</Label>
                <Label type="infoValue">{run.notes}</Label>
              </div>
            )}
          </div>

          <div className={classes.section}>
            <Label type="infoTitleSmall">Blast radius</Label>
            <div className={classes.metaGrid}>
              {Object.entries(COUNT_LABELS).map(([key, label]) => (
                <div key={key} className={classes.metaItem}>
                  <Label type="infoLabel">{label}</Label>
                  <span className={classes.countValue}>{run.counts?.[key] ?? 0}</span>
                </div>
              ))}
              <div className={classes.metaItem}>
                <Label type="infoLabel">Policies currently in force</Label>
                <span className={classes.countValue}>{run.counts?.policiesInForce ?? 0}</span>
              </div>
            </div>
          </div>

          <div className={classes.section}>
            <Label type="infoTitleSmall">Snapshot — what this run covers</Label>
            <RunArtifacts previewTaskRunId={run.previewTaskRunId} />
          </div>
        </>
      )}

      {(run.status === 'applying' || feedMessages.length > 0) && (
        <div className={classes.section}>
          <Label type="infoTitleSmall">
            {run.status === 'applying'
              ? `Applying — ${progressCount} of ${recordCount ?? '?'} records processed`
              : 'Apply progress'}
          </Label>
          <div className={classes.feed}>
            {feedMessages.map((message, index) => (
              // Append-only, so the index is stable.
              // eslint-disable-next-line react/no-array-index-key
              <Label key={index} type="infoValue">
                {message}
              </Label>
            ))}
            {!feedMessages.length && <Label type="infoLabel">Waiting for progress…</Label>}
          </div>
          {applyFeed.error && <Label type="infoLabel">{applyFeed.error}</Label>}
        </div>
      )}

      {applyReached && (
        <div className={classes.section}>
          <Label type="infoTitleSmall">Apply results</Label>
          {resultsError && <Label type="infoLabel">{`Could not load results: ${resultsError.message}`}</Label>}
          {results?.partial && (
            <Label type="infoLabel">
              This run is too large for the inline tables (stored output truncated at 1,000 rows) — use the report CSVs
              below for the complete result.
            </Label>
          )}
          {results &&
            !results.partial &&
            RESULT_VARIANTS.map((variant) => (
              <Fragment key={variant.key}>
                <ApplyResultsTable
                  title={`Applied ${variant.label}`}
                  rows={results.applied[variant.key]}
                  variant={variant}
                />
                <ApplyResultsTable
                  title={`Failed / aborted ${variant.label}`}
                  rows={results.failed[variant.key]}
                  variant={variant}
                  showError
                />
              </Fragment>
            ))}
          {results && results.filePaths.length > 0 && (
            <div className={classes.section}>
              <Label type="infoLabel">Report CSVs (complete, refreshable links)</Label>
              <div className={classes.csvList}>
                {results.filePaths.map((file) => (
                  <DownloadButton
                    key={file.output}
                    taskRunId={run.applyTaskRunId}
                    output={file.output}
                    label={REPORT_CSV_LABELS[file.output] ?? `${file.output} (full CSV)`}
                  />
                ))}
              </div>
            </div>
          )}
          {!results && !resultsError && <Label type="infoLabel">No stored results yet for this apply.</Label>}
        </div>
      )}

      {/* The whole page is already gated on canManageAor, so this is a readability boundary rather
          than a permission one: the ids needed to find the execution in CloudWatch. Collapsed by
          default — it is reference material, consulted only when something needs chasing. */}
      <CollapsibleSection className={classes.section} content={<Label type="infoTitleSmall">Additional details</Label>}>
        <div className={classes.engineerDetails}>
          <div className={classes.metaItem}>
            <Label type="infoLabel">Preview task run</Label>
            <Label type="infoValue" className={classes.monospace}>
              {run.previewTaskRunId ?? '—'}
            </Label>
          </div>
          <div className={classes.metaItem}>
            <Label type="infoLabel">Apply task run</Label>
            <Label type="infoValue" className={classes.monospace}>
              {run.applyTaskRunId ?? '—'}
            </Label>
          </div>
          <div className={classes.metaItem}>
            <Label type="infoLabel">Snapshot prefix</Label>
            <Label type="infoValue" className={classes.monospace}>
              {run.snapshotS3Prefix ?? '—'}
            </Label>
          </div>
          <div className={classes.metaItem}>
            <Label type="infoLabel">Apply task run status</Label>
            <Label type="infoValue">
              {toolDetail
                ? `${toolDetail.status ?? '—'} · started ${formatDateTime(toolDetail.startedAt)} · ended ${formatDateTime(
                    toolDetail.endedAt
                  )} · by ${toolDetail.startedBy ?? '—'}`
                : 'Not started'}
            </Label>
          </div>
          <div className={classes.metaItem}>
            <Label type="infoLabel">Last updated</Label>
            <Label type="infoValue">{`${formatDateTime(run.updatedAt)} by ${run.updatedBy ?? '—'}`}</Label>
          </div>
        </div>
      </CollapsibleSection>

      {showApprove && (
        <ApproveAndApplyDialog
          open
          run={run}
          error={approveError}
          onClose={() => {
            setShowApprove(false);
            setApproveError(null);
          }}
          onConfirm={handleConfirmApproveAndApply}
        />
      )}
      {showPreviewProgress && (
        <PreviewProgressDialog
          open
          messages={preview.messages}
          filePaths={preview.filePaths}
          taskRunId={preview.taskRunId}
          error={preview.error}
          isRunning={preview.isRunning}
          onClose={() => {
            setShowPreviewProgress(false);
            preview.reset();
          }}
        />
      )}
    </div>
  );
};

export default AorRunDetail;
